import { ClassNameProps, If, makeClassName, StyleProps } from "@hex-insights/core";
import {
	graphTypeNames,
	MessageGroupNameImageAndAvailabilityFragment,
	truncateDisplayItems,
} from "../../../../Utilities";
import * as UserComponents from "../../User";
import * as UserGroupComponents from "../../UserGroup";
import styles from "./styles.module.css";

export type MessageGroupIconProps = {
	messageGroup: Omit<MessageGroupNameImageAndAvailabilityFragment, "customName">;
	size?: number;
	title?: string;
	itemClassName?: string;
	itemStyle?: React.CSSProperties;
} & Partial<ClassNameProps & StyleProps>;

export function MessageGroupIcon({
	messageGroup,
	size = 32,
	title,
	itemClassName,
	itemStyle,
	className,
	style,
}: MessageGroupIconProps) {
	// Note: should agree (somewhat) with MessageGroupFormat.name

	const users =
		messageGroup.users.length === 1 && messageGroup.userGroups.length === 0
			? messageGroup.users
			: messageGroup.users.filter((e) => !e.isCurrent); // remove current user unless is only user
	const isSingleUser = users.length === 1 && messageGroup.userGroups.length === 0;
	if (isSingleUser) {
		return (
			<div
				title={title}
				className={makeClassName(styles["message-group-icon"], styles["message-group-icon--single"], className)}
				style={{ "--size": size, ...style } as React.CSSProperties}
			>
				<UserComponents.UserWithMessagingStatusIcon
					user={users[0]}
					size={size}
					title={title}
					className={itemClassName}
					style={itemStyle}
				/>
			</div>
		);
	}

	if (messageGroup.userGroups.length > 0 || users.length > 0) {
		const members = [...messageGroup.userGroups, ...users];
		const { displayedItems, anyHidden, numHidden } = truncateDisplayItems(members, 3);
		const isSingleItem = displayedItems.length === 1;

		const mainUserSize = isSingleItem ? size : (size * 2) / 3;
		const mainUserGroupSize = isSingleItem ? size : size * 0.6;
		const smallItemSize = (size * 2) / 5;

		return (
			<div
				title={title}
				className={makeClassName(
					styles["message-group-icon"],
					isSingleItem ? styles["message-group-icon--single"] : "",
					className,
				)}
				style={{ "--size": `${size}px`, ...style } as React.CSSProperties}
			>
				{displayedItems.map((member, i) => {
					if (member.__typename === graphTypeNames.UserGroup) {
						return (
							<UserGroupComponents.UserGroupIcon
								key={i}
								userGroup={member}
								size={i <= 1 ? mainUserGroupSize : smallItemSize}
								className={makeClassName(
									styles["message-group-icon__member-icon"],
									styles["message-group-icon__member-icon--with-border"],
									itemClassName,
								)}
								style={itemStyle}
							/>
						);
					}

					return (
						<UserComponents.UserIcon
							key={i}
							user={member as UserComponents.IconUser}
							size={i <= 1 ? mainUserSize : smallItemSize}
							className={makeClassName(styles["message-group-icon__member-icon"], itemClassName)}
							iconClassName={makeClassName(styles["message-group-icon__member-icon--with-border"])}
							style={itemStyle}
						/>
					);
				})}

				<If condition={anyHidden}>
					<UserComponents.UserAdditionalCountIcon
						count={numHidden}
						size={smallItemSize}
						className={makeClassName(
							styles["message-group-icon__member-icon"],
							styles["message-group-icon__member-icon--with-border"],
							itemClassName,
						)}
						style={itemStyle}
					/>
				</If>
			</div>
		);
	}

	return null;
}
